import { useState } from "react";
import { Link } from "react-router-dom";
import { Buildings, MagnifyingGlass, ArrowLeft, Package } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import api, { formatApiError } from "@/lib/api";
import { JOB_STATUS_LABELS, JOB_STATUS_COLORS } from "@/lib/constants";
import JobTimeline from "@/components/JobTimeline";

export default function Track() {
  const [ref, setRef] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const search = async (e) => {
    e.preventDefault();
    setError(""); setResult(null); setLoading(true);
    try {
      const { data } = await api.get(`/public/track/${encodeURIComponent(ref.trim())}`);
      setResult(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Not found");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#0B0C10] text-white noise-overlay">
      <header className="backdrop-blur-xl bg-black/40 border-b border-white/10">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="track-brand">
            <div className="w-9 h-9 rounded-md bg-white/10 flex items-center justify-center"><Buildings weight="fill" className="text-accent" size={20} /></div>
            <span className="font-display font-bold text-lg">Aakriti Associates</span>
          </Link>
          <Link to="/" className="inline-flex items-center gap-1 text-sm text-white/70 hover:text-white transition-colors" data-testid="track-back"><ArrowLeft size={16} /> Home</Link>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-6 py-16">
        <div className="text-xs tracking-[0.2em] uppercase font-bold text-accent">Live Status</div>
        <h1 className="font-display font-black text-4xl mt-3">Track Your Valuation</h1>
        <p className="text-white/70 mt-3">Enter the reference number you received when submitting the request.</p>

        <form onSubmit={search} className="mt-8 flex gap-3" data-testid="track-form">
          <div className="relative flex-1">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" size={18} />
            <Input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="VAL-260826-XXXX" className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/40 uppercase font-mono" data-testid="track-input" required />
          </div>
          <Button type="submit" disabled={loading} className="rounded-full bg-accent hover:bg-accent/90 text-accent-foreground font-semibold" data-testid="track-submit">{loading ? "Searching…" : "Track"}</Button>
        </form>

        {error && <div className="mt-6 text-rose-300 text-sm" data-testid="track-error">{error}</div>}

        {result && (
          <Card className="mt-8 p-6 bg-white text-foreground" data-testid="track-result">
            <div className="flex items-start justify-between gap-3 border-b border-border pb-4">
              <div>
                <div className="font-mono text-sm text-muted-foreground">{result.reference}</div>
                <div className="font-display text-xl font-semibold text-primary mt-1">{result.applicant_name}</div>
                <div className="text-sm text-muted-foreground">{result.property_type} · {result.bank_name}{result.branch_name ? ` — ${result.branch_name}` : ""}</div>
              </div>
              <span className={`text-xs px-2.5 py-1 rounded border whitespace-nowrap ${JOB_STATUS_COLORS[result.status]}`}>{JOB_STATUS_LABELS[result.status]}</span>
            </div>
            <div className="mt-5">
              <div className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground mb-4 flex items-center gap-2"><Package size={16} weight="duotone" className="text-accent" /> Progress</div>
              <JobTimeline status={result.status} statusHistory={result.status_history} />
            </div>
            {result.deadline && <div className="mt-4 text-sm text-muted-foreground">Target completion: <span className="font-mono text-primary">{result.deadline.slice(0, 10)}</span></div>}
          </Card>
        )}
      </div>
    </div>
  );
}
